import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { useHasDisapprovalWorkflow } from "@/pages/(app)/notifications/disapproval-reasons/hooks/use-has-disapproval-workflow";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Label } from "@babackapp/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/ui/components/radio-group";
import { Separator } from "@babackapp/ui/components/separator";
import { Textarea } from "@babackapp/ui/components/textarea";
import { cn } from "@babackapp/ui/lib/utils";
import { PencilLine, WandSparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { resolveReasonMessage } from "./resolve-reason-message";
import { ReturnSummary } from "./return-summary";
import { SaveAsReasonForm } from "./save-as-reason-form";

const FREE_TEXT = "free-text" as const;

export type DisapprovalPayload = {
  disapprovalReasonId?: string;
  disapprovalMessage: string;
};

type Props = {
  returnRequest: ReturnRequestDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (payload: DisapprovalPayload) => void;
  isPending: boolean;
};

export function DisapproveReturnDialog({
  returnRequest,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: Props) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const hasWorkflow = useHasDisapprovalWorkflow(shop.id);
  const canCreateReason = useViewerHasAtLeastAdminRights();

  const reasons = shop.disapprovalReasons.filter(
    (reason) => !reason.archivedAt,
  );

  const [selected, setSelected] = useState<string>(FREE_TEXT);
  const [message, setMessage] = useState("");
  // Survives a detour through a preset: comparing wordings must not cost the
  // merchant the paragraph they just wrote.
  const [freeTextDraft, setFreeTextDraft] = useState("");
  const [isSavingAsReason, setIsSavingAsReason] = useState(false);

  const preset = reasons.find((reason) => reason.id === selected);
  const presetMessage = preset
    ? resolveReasonMessage(preset.message, returnRequest.lang)
    : "";
  const isEdited = Boolean(preset) && message !== presetMessage;

  // Reopening on another return must not carry the previous answer over.
  useEffect(() => {
    if (!open) return;
    setSelected(FREE_TEXT);
    setMessage("");
    setFreeTextDraft("");
    setIsSavingAsReason(false);
  }, [open, returnRequest.id]);

  const onSelect = (value: string) => {
    if (selected === FREE_TEXT) setFreeTextDraft(message);
    setSelected(value);
    setIsSavingAsReason(false);

    const next = reasons.find((reason) => reason.id === value);
    setMessage(
      next
        ? resolveReasonMessage(next.message, returnRequest.lang)
        : freeTextDraft,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100vh-64px)] max-w-3xl flex-col gap-5 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("disapproveReturn.title")}</DialogTitle>
          <DialogDescription>
            {/* Only promise an email when a workflow will actually send one;
                without it the reason stops at the return's activity feed. */}
            {hasWorkflow
              ? t("disapproveReturn.descriptionWithEmail")
              : t("disapproveReturn.description")}
          </DialogDescription>
        </DialogHeader>

        <ReturnSummary returnRequest={returnRequest} />
        <Separator />

        <div className="flex flex-col gap-2">
          <Label>{t("disapproveReturn.reasonLabel")}</Label>
          <RadioGroup
            value={selected}
            onValueChange={onSelect}
            className="gap-2"
          >
            {reasons.map((reason) => (
              <ReasonOption
                key={reason.id}
                value={reason.id}
                title={reason.label}
                help={reason.help}
                isSelected={selected === reason.id}
              />
            ))}
            <ReasonOption
              value={FREE_TEXT}
              title={t("disapproveReturn.freeText.title")}
              help={t("disapproveReturn.freeText.help")}
              isSelected={selected === FREE_TEXT}
            />
          </RadioGroup>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="disapproval-message">
              {hasWorkflow
                ? t("disapproveReturn.message.label")
                : t("disapproveReturn.message.labelNoEmail")}
              {!preset && ` ${t("disapproveReturn.message.required")}`}
            </Label>
            <Badge variant="secondary" className="gap-1 font-normal">
              {preset ? (
                <>
                  <WandSparkles className="size-3" />
                  {t("disapproveReturn.message.filledByReason")}
                </>
              ) : (
                <>
                  <PencilLine className="size-3" />
                  {t("disapproveReturn.message.toWrite")}
                </>
              )}
            </Badge>
          </div>

          <Textarea
            id="disapproval-message"
            rows={3}
            className="resize-none"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={
              preset ? undefined : t("disapproveReturn.message.placeholder")
            }
            maxLength={1000}
          />

          <p className="text-xs text-muted-foreground">
            {preset
              ? t("disapproveReturn.message.presetHelp")
              : t("disapproveReturn.message.freeTextHelp")}
          </p>

          {isEdited && (
            <button
              type="button"
              className="self-start text-xs underline"
              onClick={() => setMessage(presetMessage)}
            >
              {t("disapproveReturn.message.restore")}
            </button>
          )}

          {/* Creating a reason is ADMIN/OWNER only, while disapproving is open
              to customer support — hiding the link beats a 403. */}
          {!preset &&
            message.trim() &&
            canCreateReason &&
            !isSavingAsReason && (
              <button
                type="button"
                className="self-start text-xs underline"
                onClick={() => setIsSavingAsReason(true)}
              >
                {t("disapproveReturn.message.saveAsReason")}
              </button>
            )}

          {isSavingAsReason && message.trim() && (
            <SaveAsReasonForm
              message={message.trim()}
              lang={returnRequest.lang}
              // Selecting it closes the loop: without this the disapproval that
              // created the reason would be the one recorded without it.
              onSaved={(reasonId) => {
                setIsSavingAsReason(false);
                setSelected(reasonId);
              }}
              onCancel={() => setIsSavingAsReason(false)}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("misc.cancel")}
          </Button>
          <ButtonLoading
            variant="destructive"
            isLoading={isPending}
            disabled={!message.trim()}
            onClick={() =>
              onConfirm({
                disapprovalReasonId: preset?.id,
                disapprovalMessage: message.trim(),
              })
            }
          >
            {t("disapproveReturn.confirm")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonOption({
  value,
  title,
  help,
  isSelected,
}: {
  value: string;
  title: string;
  help?: string | null;
  isSelected: boolean;
}) {
  return (
    <Label
      htmlFor={`disapproval-reason-${value}`}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors duration-200",
        isSelected ? "border-primary bg-accent" : "border-border",
      )}
    >
      <RadioGroupItem
        id={`disapproval-reason-${value}`}
        value={value}
        className="mt-0.5"
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{title}</span>
        {help && (
          <span className="text-xs font-normal text-muted-foreground">
            {help}
          </span>
        )}
      </span>
    </Label>
  );
}
